/*
Copyright (C) 2020 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/

import { getMerchandiseTotal } from '@broadleaf/admin-components/dist/oms/utils/FulfillmentUtils';
import { ICommonMonetaryAmount } from '@broadleaf/admin-components/dist/types/common';
import {
  IOrder,
  IOrderFulfillment
} from '@broadleaf/admin-components/dist/types/oms';
import { includes, map } from 'lodash';

export type IOrderFulfillmentSettings = {
  fulfilledByEmail: string;
  fulfillmentOverrideOption: string;
  revMedFulfillmentFee: number;
  revMedFeeAmountOverride: number;
  revMedFeePercentOverride: number;
  maxRevMedFulfillmentFee: number;
  blindShipment: boolean;
  includeShippingInsurance: boolean;
};
export type IOrderFulfillmentCommissions = {
  totalAmount: number;
  totalPercentage: number;
  commissionLineItems: Array<IOrderFulfillmentCommissionLineItem>;
};
export type IFulfillmentSellerPaymentSummary = {
  totalAmount: number;
  sellerPayments: Array<ISellerPayment>;
};

export type ISellerPayment = {
  id: string;
  fulfillmentId: string;
  amount: number;
  paymentDate: string;
  reason: string;
  reasonDescription: string;
  paymentSourceType: string;
  paymentSourceId: string;
};

export type IOrderFulfillmentCommissionLineItem = {
  amount: number;
  percentage: number;
  reason: string;
  email: string;
};

export type IConsolidatedOrder = IOrder & {
  revmedCreditAccount: ICreditAccount;
  invoiceCreditAccount: ICreditAccount;
  invoiceSummary: IInvoiceSummary;
  accountId: string;
};

export type ICreditAccount = {
  id: string;
  type: string;
  accountNumber: string;
  owningUserType: string;
  owningUserRef: string;
  balance: ICommonMonetaryAmount;
  attributes: Record<string, unknown>;
};

export type IInvoicePayment = {
  id: string;
  invoiceId: string;
  amount: number;
  paymentDate: string;
  reason: string;
  reasonDescription: string;
  paymentSourceType: string;
  paymentSourceId: string;
};

export type IInvoiceSummary = {
  invoiceId: string;
  invoiceCreditProvider: string;
  orderSubmitDate: string;
  invoiceStatus: string;
  invoiceStatusReason: string;
  remitraExportId: string;
  remitraExportName: string;
  remitraExportTime: string;
  orderNumber: string;
  invoiceNumber: string;
  poNumber: string;
  customerName: string;
  emailAddress: string;
  invoiceDate: string;
  invoiceDueDate: string;
  shipToAccountId: string;
  shipToAddressLine1: string;
  shipToAddressLine2: string;
  shipToOrganization: string;
  shipToCity: string;
  shipToStateProvinceRegion: string;
  shipToPostalCode: string;
  billToAccountId: string;
  billToAddressLine1: string;
  billToAddressLine2: string;
  billToOrganization: string;
  billToCity: string;
  billToStateProvinceRegion: string;
  billToPostalCode: string;
  invoicePayments: IInvoicePayment[];
  invoicePaymentsTotal: ICommonMonetaryAmount;
  invoiceBalance: ICommonMonetaryAmount;
  merchandiseTotal: ICommonMonetaryAmount;
  revMedProcessingFee: ICommonMonetaryAmount;
  revMedCreditAmount: ICommonMonetaryAmount;
  fulfillmentTotal: ICommonMonetaryAmount;
  taxTotal: ICommonMonetaryAmount;
  grandTotal: ICommonMonetaryAmount;
};

export type ISalesInfo = {
  salesRep: ISalesRep;
  vendorRef: string;
  accountId: string;
  accountName: string;
};

export type ISalesRep = {
  name: string;
  email: string;
};

export function getAttributeAmount(fulfillment, attribute, negate = false) {
  const amount =
    typeof fulfillment.attributes[attribute] === 'number'
      ? fulfillment.attributes[attribute]
      : 0;
  return {
    amount: amount * (negate && amount !== 0 ? -1 : 1),
    currency: fulfillment.currency
  };
}

export function getMaxRevMedFee(
  fulfillment: IOrderFulfillment
): ICommonMonetaryAmount {
  const merchandiseTotal = getMerchandiseTotal(fulfillment);
  const insuranceCost = getAttributeAmount(fulfillment, 'insuranceCost');
  return {
    amount: merchandiseTotal.amount - insuranceCost.amount,
    currency: fulfillment.currency
  };
}

export function getMaxFulfillmentFee(
  fulfillment: IOrderFulfillment,
  revMedFees: ICommonMonetaryAmount = getAttributeAmount(
    fulfillment,
    'revMedFee'
  )
): ICommonMonetaryAmount {
  return {
    amount: getMaxRevMedFee(fulfillment).amount - revMedFees.amount,
    currency: fulfillment.currency
  };
}

export function hasPackingSlipBeenPrinted(
  orderFulfillment: IOrderFulfillment
): boolean {
  return !!orderFulfillment.attributes['printPackingSlipDateTime'];
}

export function hasShippingDocuments(
  orderFulfillment: IOrderFulfillment
): boolean {
  return !!orderFulfillment.attributes['shippingDocuments'];
}

export function hasPendingApprovalStatusChangeRequest(
  orderFulfillment: IOrderFulfillment,
  nextStatus: string
): boolean {
  return (
    !!orderFulfillment.attributes['pendingApprovalStatusChangeRequest'] &&
    orderFulfillment.attributes['pendingApprovalStatusChangeRequest'].status ===
      nextStatus
  );
}

export function getActionStatus(
  nextStatus: string,
  orderFulfillment: IOrderFulfillment
) {
  if (
    includes(
      [
        'CAPTURING_PAYMENT',
        'PRINT_PACKING_SLIP',
        'PRINT_SHIPPING_LABEL',
        'PRINT_SHIPPING_DOCUMENTS'
      ],
      nextStatus
    ) &&
    hasPendingApprovalStatusChangeRequest(orderFulfillment, 'CANCELLED')
  ) {
    return {
      isDisabled: true,
      actionTooltip:
        'The cancellation must be approved before continuing. Any printed shipping label or packing slip should be discarded'
    };
  }

  if (includes(['PRINT_SHIPPING_DOCUMENTS'], nextStatus)) {
    return {
      isDisabled: false,
      actionTooltip: 'Hold Shift + R to force regenerate the shipping documents'
    };
  }

  return {
    isDisabled: false,
    actionTooltip: ''
  };
}

export const getHasSaturdayDelivery = (fg?: IOrderFulfillment): boolean => {
  return (
    getAttributeValue(
      fg?.attributes,
      'saturdayDelivery',
      getAttributeValue(
        fg?.address as Record<string, string>,
        'saturdayDelivery',
        'false'
      )
    ) +
      '' ===
    'true'
  );
};

export const getHasBlindShipment = (fg?: IOrderFulfillment): boolean => {
  return (
    getAttributeValue(
      fg?.attributes,
      'blindShipment',
      getAttributeValue(
        fg?.address as Record<string, string>,
        'blindShipment',
        'false'
      )
    ) +
      '' ===
    'true'
  );
};

export const getIncludeShippingInsurance = (
  fg?: IOrderFulfillment
): boolean => {
  return (
    getAttributeValue(
      fg?.attributes,
      'includeShippingInsurance',
      getAttributeValue(
        fg?.address as Record<string, string>,
        'includeShippingInsurance',
        'true'
      )
    ) === 'true'
  );
};

export const getShippingAccountType = (fg?: IOrderFulfillment): string => {
  return getAttributeValue(
    fg?.attributes,
    'shippingAccountType',
    getAttributeValue(
      fg?.address as Record<string, string>,
      'shippingAccountType',
      undefined
    )
  ) as string;
};

export const getShippingAccountNumber = (fg?: IOrderFulfillment): string => {
  return getAttributeValue(
    fg?.attributes,
    'shippingAccountNumber',
    getAttributeValue(
      fg?.address as Record<string, string>,
      'shippingAccountNumber',
      undefined
    )
  ) as string;
};

export const getShippingAccountPostalCode = (
  fg?: IOrderFulfillment
): string => {
  return getAttributeValue(
    fg?.attributes,
    'shippingAccountPostalCode',
    getAttributeValue(
      fg?.address as Record<string, string>,
      'shippingAccountPostalCode',
      undefined
    )
  ) as string;
};

export const getVendorNames = (fg?: IOrderFulfillment): string[] => {
  const fulfillmentSalesInfo = getAttributeValue(
    fg?.attributes,
    'fulfillmentSalesInfo'
  ) as any;

  if (!fulfillmentSalesInfo) {
    return [''];
  }

  if (fg.vendorRef) {
    return [
      fulfillmentSalesInfo.sellerSalesInfos.find(
        sellerSalesInfo => sellerSalesInfo.vendorRef === fg.vendorRef
      )?.accountName || ''
    ];
  }

  return map(
    fulfillmentSalesInfo.sellerSalesInfos,
    salesInfo => salesInfo.accountName
  );
};

export const getSellerFacilities = (fg?: IOrderFulfillment): ISalesInfo[] => {
  const fulfillmentSalesInfo = getAttributeValue(
    fg?.attributes,
    'fulfillmentSalesInfo'
  ) as any;

  if (!fulfillmentSalesInfo) {
    return [];
  }

  if (fg.vendorRef) {
    return [
      fulfillmentSalesInfo.sellerSalesInfos.find(
        sellerSalesInfo => sellerSalesInfo.vendorRef === fg.vendorRef
      )
    ];
  }

  return fulfillmentSalesInfo.sellerSalesInfos;
};

export const getBuyerFacility = (fg?: IOrderFulfillment): ISalesInfo => {
  const fulfillmentSalesInfo = getAttributeValue(
    fg?.attributes,
    'fulfillmentSalesInfo'
  ) as any;

  if (!fulfillmentSalesInfo) {
    return {} as ISalesInfo;
  }

  return fulfillmentSalesInfo.buyerSalesInfo || {};
};

export const getInventoryLocationName = (fg?: IOrderFulfillment): string => {
  const fulfillmentSalesInfo = getAttributeValue(
    fg?.attributes,
    'fulfillmentSalesInfo'
  ) as any;

  if (!fulfillmentSalesInfo) {
    return '';
  }

  return fulfillmentSalesInfo.inventoryLocationName || '';
};

export const hasHideSellerDetails = (entity?: IOrderFulfillment): boolean => {
  if (entity?.attributes?.hideSellerDetails === undefined) {
    return false;
  } else {
    return (
      entity?.attributes?.hideSellerDetails === true ||
      entity?.attributes?.hideSellerDetails === 'true' ||
      entity?.attributes?.hideSellerDetails['value'] === true ||
      entity?.attributes?.hideSellerDetails['value'] === 'true'
    );
  }
};

export const getCarrierFriendlyName = (fg: IOrderFulfillment) => {
  const type = getShippingAccountType(fg);
  if (type === 'ups') {
    return 'UPS';
  } else {
    return 'FedEx';
  }
};

export const getShippingOptionMessages = (fg?: IOrderFulfillment): string[] => {
  const fulfillmentOption = fg?.fulfillmentOption;

  const shippingOptionMessages = [];

  //Some messages only apply if you choose shipengine
  if (fulfillmentOption && fulfillmentOption?.name?.startsWith('SHIPENGINE')) {
    const shippingAccountType = getShippingAccountType(fg);
    if (shippingAccountType) {
      const shippingAccountNumber = getShippingAccountNumber(fg);
      const carrier = getCarrierFriendlyName(fg);
      shippingOptionMessages.push(
        `Billed to ${carrier} #${shippingAccountNumber}`
      );
    }

    const hasSaturdayDelivery = getHasSaturdayDelivery(fg);
    if (hasSaturdayDelivery) {
      shippingOptionMessages.push(`Saturday Delivery`);
    }
  }

  const hasBlindShipment = getHasBlindShipment(fg);
  if (hasBlindShipment) {
    shippingOptionMessages.push('Blind Shipment');
  }
  return shippingOptionMessages;
};

export function getAttributeValue(
  attributes: Record<string, unknown> | Record<string, any>,
  attributeName: string,
  defaultValue: any = undefined
): unknown {
  if (
    attributes?.[attributeName] !== null &&
    attributes?.[attributeName] !== undefined
  ) {
    if (
      Object.prototype.hasOwnProperty.call(attributes[attributeName], 'value')
    ) {
      return attributes[attributeName]['value'];
    } else {
      return attributes[attributeName];
    }
  }
  return defaultValue;
}

const revMedSalesUsers = [
  { value: 'dcrittenden@joinrevmed.com', label: 'Dan Crittenden' },
  { value: 'bhood@revmedconnect.com', label: 'Brandon Hood' },
  { value: 'khoffman@revmedconnect.com', label: 'Kara Hoffman' },
  { value: 'kmorera@revmedconnect.com', label: 'Kevin Morera' }
];

export function getRevMedSalesUsers() {
  return revMedSalesUsers;
}
